import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'
import { capitalize } from '../../../util'
import { DesignSystemTheme } from '../../design-system'
import { PlaceholderStateComponent, PlaceholderStateSize } from '../placeholder-state'

export const ErrorStateVariant = {
  Custom: 'custom',
  FailedToLoad: 'failed-to-load',
} as const
export type ErrorStateVariant = (typeof ErrorStateVariant)[keyof typeof ErrorStateVariant]

/**
 * Thin wrapper component over `ftr-placeholder-state`. Displays an error state for when content
 * has failed to load.
 *
 * @example
 * ```html
 * <!-- Default usage (failed to load entity) -->
 * <ftr-error-state entityName="entities" (action)="reload()" />
 *
 * <!-- Customised usage (provide variant="custom" to customise title/message/actionText) -->
 * <ftr-error-state
 *   variant="custom"
 *   message="Something has gone terribly wrong."
 *   actionText="Try Again"
 *   size="small"
 * />
 */
@Component({
  selector: 'ftr-error-state',
  templateUrl: './error-state.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PlaceholderStateComponent],
})
export class ErrorStateComponent {
  /** To customise title/message/actionText, use variant="custom". */
  readonly variant = input<ErrorStateVariant>(ErrorStateVariant.FailedToLoad)
  readonly entityName = input<string>()
  readonly size = input<PlaceholderStateSize>(PlaceholderStateSize.FullPage)
  readonly title = input<string>()
  readonly message = input<string>()
  readonly actionText = input<string>()

  readonly action = output()

  protected readonly errorTheme = DesignSystemTheme.Error

  protected readonly resolvedConfig = computed<ErrorStateConfig>(() => {
    const config = getErrorStateConfig(this.variant(), this.entityName())
    return {
      title: this.title() || config.title,
      message: this.message() || config.message,
      actionText: this.actionText() || config.actionText,
    }
  })
}

interface ErrorStateConfig {
  title?: string
  message?: string
  actionText?: string
}

function getErrorStateConfig(variant: ErrorStateVariant, entityName?: string): ErrorStateConfig {
  switch (variant) {
    case ErrorStateVariant.FailedToLoad:
      return {
        title: entityName ? `Failed to Load ${capitalize(entityName)}` : 'Failed to Load',
        message: 'Please reload the page or try again later.',
        actionText: 'Reload Page',
      }
    case ErrorStateVariant.Custom:
    default:
      return {}
  }
}
